/**
 * Permission helpers derived from the current user's app role assignments.
 *
 * Mirrors the BFF-side requireAdminRead / requireAdminWrite gates so the
 * UI can disable interactive controls for an Admin.Read user instead of
 * letting them click a button that the server is just going to 403.
 *
 * Source of truth is the `roles` array on useCurrentUser, which is
 * populated from the access token's `roles` claim. The arrays are
 * reactive — when a role changes server-side and the user refreshes,
 * these flags update on the next /api/auth/me round-trip.
 */
import { computed } from 'vue'

export function usePermissions() {
  const { user } = useCurrentUser()

  const roles = computed<string[]>(() => user.value?.roles ?? [])

  // Plain "Admin" is the legacy single role that pre-dates the
  // Read/Write split; treat it as full write for backwards compat.
  const isAdminWrite = computed(() => roles.value.some(r => r === 'Admin' || r === 'Admin.Write'))
  const isAdminRead  = computed(() => roles.value.some(r => r === 'Admin' || r === 'Admin.Read' || r === 'Admin.Write'))

  // ---------------------------------------------------------------------------
  // ADVISOR-RESTRICTED ADMIN
  //
  // Advisors get a deliberately narrow slice of the admin console: Manage Users
  // and Refresh Households, and within those only CLIENT users belonging to
  // NON-EMPLOYEE households. Everything else is hidden.
  //
  // "Advisor-restricted" means Advisor WITHOUT any admin role — an admin who is
  // also an Advisor (see the admin-advisor accounts) keeps full admin rights, so
  // the check is deliberately `isAdvisor && !isAdminRead` rather than just
  // `isAdvisor`.
  //
  // These flags drive UI only. Every restriction is enforced again in the BFF
  // (server/utils/admin-auth.ts → requireAdminOrAdvisor) because hiding a button
  // is not access control.
  // ---------------------------------------------------------------------------
  const isAdvisor = computed(() => roles.value.includes('Advisor'))
  const isAdvisorRestrictedAdmin = computed(() => isAdvisor.value && !isAdminRead.value)

  /** Admin routes an advisor-restricted user may open. Anything else → /forbidden. */
  const ADVISOR_ADMIN_ROUTES = ['/admin/users', '/admin/refresh-households']

  const canViewAdminConsole = computed(() => isAdminRead.value || isAdvisorRestrictedAdmin.value)

  /** True when the caller may only ever act on Client-role users. */
  const clientsOnly = isAdvisorRestrictedAdmin

  // The hook most components actually want: "is the user allowed to
  // submit/toggle/destroy in admin pages?" Naming this `canWrite` keeps
  // the bind sites readable: `:disabled="!canWrite"`.
  // Advisors DO get write affordances — they may add and manage clients — so
  // canWrite is true for them. What they can act ON is narrowed by clientsOnly
  // and enforced server-side, rather than by withholding write entirely.
  const canWrite = computed(() => isAdminWrite.value || isAdvisorRestrictedAdmin.value)
  const canRead  = canViewAdminConsole

  /**
   * Convenient tooltip text for disabled controls — explains why the
   * action is off-limits instead of leaving the user wondering.
   */
  const writeDisabledReason = computed(() =>
    canWrite.value
      ? ''
      : 'Admin.Write required. Your account has read-only access to this page.'
  )

  /** Shown when an advisor hits a control that only applies to non-client users. */
  const clientsOnlyReason = computed(() =>
    clientsOnly.value
      ? 'Advisors can only view and manage client accounts.'
      : ''
  )

  return {
    roles,
    isAdminRead,
    isAdminWrite,
    isAdvisor,
    isAdvisorRestrictedAdmin,
    canViewAdminConsole,
    clientsOnly,
    clientsOnlyReason,
    ADVISOR_ADMIN_ROUTES,
    canRead,
    canWrite,
    writeDisabledReason
  }
}
